/**
 * SCORM 1.2 adapter — bridges scorm-again's Scorm12API with our
 * backend CMI persistence (commit/restore via Session JWT).
 *
 * Supports both same-origin (direct API injection) and cross-origin
 * (postMessage proxy) communication with the SCORM iframe.
 *
 * Usage:
 *   const adapter = new Scorm12Adapter(config);
 *   adapter.initialize();         // sets up scorm-again + listeners
 *   adapter.loadIntoFrame(iframe); // injects API into iframe
 *   adapter.destroy();            // cleanup
 */

import type {
    ScormAdapterConfig,
    ScormCommitResult,
    ScormCompletionEvent,
} from './types';
import { commitCmi, callApiDb } from './api';

// scorm-again is imported dynamically to avoid SSR issues
let Scorm12API: any = null;

async function loadScorm12API() {
    if (!Scorm12API) {
        const mod = await import('scorm-again');
        Scorm12API = (mod as any).Scorm12API ?? (mod as any).default?.Scorm12API;
    }
    return Scorm12API;
}

/** Max frame depth walked when matching a message source to the SCORM iframe. */
const MAX_FRAME_DEPTH = 10;

/**
 * True when `source` is `frameWindow` or a window nested inside it. Walks `source.parent`,
 * which is readable cross-origin, so CDN-hosted nested SCOs still match.
 */
export function isFromFrame(source: Window | null, frameWindow: Window | null): boolean {
    if (!source || !frameWindow) return false;
    let current: Window = source;
    for (let depth = 0; depth < MAX_FRAME_DEPTH; depth += 1) {
        if (current === frameWindow) return true;
        let parent: Window | null;
        try {
            parent = current.parent;
        } catch {
            return false;
        }
        if (!parent || parent === current) return false;
        current = parent;
    }
    return false;
}

export class Scorm12Adapter {
    private config: ScormAdapterConfig;
    private api: any = null;
    private commitTimer: ReturnType<typeof setInterval> | null = null;
    private destroyed = false;
    private messageHandler: ((e: MessageEvent) => void) | null = null;
    private apiDbProxy: Record<string, (...args: any[]) => any> | null = null;

    constructor(config: ScormAdapterConfig) {
        this.config = {
            autoCommitIntervalMs: 30_000,
            ...config,
        };
    }

    /**
     * Initialize the scorm-again SCORM 1.2 API instance
     * and set up event listeners for commit/completion.
     */
    async initialize(): Promise<void> {
        const API = await loadScorm12API();

        this.api = new API({
            autocommit: false, // We handle commit ourselves
            autocommitSeconds: 0,
            logLevel: 1, // Errors only
            selfReportSessionTime: true,
            lmsCommitUrl: '', // We override commit manually
        });

        // Pre-populate CMI data if resuming a session
        if (this.config.initialCmiData && Object.keys(this.config.initialCmiData).length > 0) {
            this.api.loadFromJSON(this.config.initialCmiData);
        }

        // Set learner info
        if (this.config.learnerId) {
            this.api.setCMIValue('cmi.core.student_id', this.config.learnerId);
        }
        if (this.config.learnerName) {
            this.api.setCMIValue('cmi.core.student_name', this.config.learnerName);
        }

        // Listen for LMSCommit
        this.api.on('LMSCommit', () => {
            this.handleCommit();
        });

        // Listen for completion
        this.api.on('LMSSetValue.cmi.core.lesson_status', (_: string, value: string) => {
            if (value === 'passed' || value === 'completed' || value === 'failed') {
                this.handleCompletion();
            }
        });

        // Auto-commit timer
        if (this.config.autoCommitIntervalMs && this.config.autoCommitIntervalMs > 0) {
            this.commitTimer = setInterval(() => {
                this.handleCommit();
            }, this.config.autoCommitIntervalMs);
        }
    }

    /**
     * Inject the SCORM 1.2 API into an iframe's contentWindow.
     * SCORM 1.2 expects `window.API` on the parent or opener.
     *
     * For cross-origin iframes, sets up a postMessage bridge so the
     * SCO's injected proxy can communicate with scorm-again.
     */
    loadIntoFrame(iframe: HTMLIFrameElement): void {
        if (!this.api) {
            throw new Error('Scorm12Adapter not initialized. Call initialize() first.');
        }

        // Build API_DB proxy that calls the backend apidb endpoint
        this.apiDbProxy = this.buildApiDbProxy();

        let isSameOrigin = false;
        try {
            // SCORM 1.2 content looks for window.API on parent frames
            const iframeWindow = iframe.contentWindow;
            if (iframeWindow) {
                (iframeWindow as any).API = this.api;
                (iframeWindow as any).API_1484_11 = this.api;
                (iframeWindow as any).API_DB = this.apiDbProxy;
                isSameOrigin = true;
            }
        } catch {
            // Cross-origin restriction — will use postMessage bridge
            isSameOrigin = false;
        }

        // Also set on current window so parent-chain walking finds them
        (window as any).API = this.api;
        (window as any).API_1484_11 = this.api;
        (window as any).API_DB = this.apiDbProxy;

        // Also provide getScormAPIForFrame helper (used by backend adapter)
        (window as any).getScormAPIForFrame = () => ({
            API: this.api,
            API_DB: this.apiDbProxy,
        });

        // Set up postMessage listener for cross-origin proxy communication
        this.setupPostMessageBridge(iframe);
    }

    /**
     * Build an API_DB proxy object that forwards QCO operations to the backend.
     * The SCO calls API_DB.createQcoInstance / putIfAbsent / confirm.
     */
    private buildApiDbProxy(): Record<string, (...args: any[]) => any> {
        const config = this.config;
        return {
            createQcoInstance: (qid: any, u: any, max: any, fv: any) => {
                callApiDb(config.sessionId, config.sessionToken, 'createQcoInstance', {
                    params: [qid, u, max, fv],
                }).catch((e) => console.warn('[SCORM API_DB] createQcoInstance error:', e));
                return {}; // SCO doesn't use the return value
            },
            putIfAbsent: (qco: any) => {
                callApiDb(config.sessionId, config.sessionToken, 'putIfAbsent', {
                    params: [qco],
                }).catch((e) => console.warn('[SCORM API_DB] putIfAbsent error:', e));
                return {};
            },
            confirm: (qco: any, data: any) => {
                callApiDb(config.sessionId, config.sessionToken, 'confirm', {
                    params: [qco, data],
                }).catch((e) => console.warn('[SCORM API_DB] confirm error:', e));
                return {};
            },
        };
    }

    /**
     * Listen for postMessage events from the SCO iframe's cross-origin proxy.
     * The backend-injected adapter script sends messages with `_scorm: true`.
     */
    private setupPostMessageBridge(iframe: HTMLIFrameElement): void {
        // Remove previous handler if re-injecting
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
        }

        this.messageHandler = (e: MessageEvent) => {
            if (!e.data || !e.data._scorm) return;
            // EAA-1644: accept SCORM calls only from the SCORM iframe or a frame nested in
            // it (multi-SCO packages). Any other window on the page — an ad, another embed,
            // a hostile opener — could otherwise drive LMSSetValue/commit for this learner.
            const sourceWindow = e.source as Window | null;
            if (!isFromFrame(sourceWindow, iframe.contentWindow)) return;

            const { id, type, data } = e.data;

            switch (type) {
                case 'LMSInitialize':
                    this.api?.lmsInitialize?.() ?? this.api?.LMSInitialize?.();
                    break;
                case 'LMSSetValue':
                    if (data?.key != null) {
                        this.api?.lmsSetValue?.(data.key, data.value) ??
                            this.api?.LMSSetValue?.(data.key, data.value);
                    }
                    break;
                case 'LMSCommit':
                    // If SCO sent full CMI cache, load it into scorm-again
                    if (data?.cmi && typeof data.cmi === 'object') {
                        for (const [key, value] of Object.entries(data.cmi)) {
                            try {
                                this.api?.setCMIValue?.(key, value);
                            } catch { /* skip invalid keys */ }
                        }
                    }
                    this.handleCommit();
                    break;
                case 'LMSFinish':
                    if (data?.cmi && typeof data.cmi === 'object') {
                        for (const [key, value] of Object.entries(data.cmi)) {
                            try {
                                this.api?.setCMIValue?.(key, value);
                            } catch { /* skip invalid keys */ }
                        }
                    }
                    this.handleCommit();
                    break;
                case 'createQcoInstance':
                    this.apiDbProxy?.createQcoInstance(
                        data?.qid, data?.u, data?.max, data?.fv,
                    );
                    break;
                case 'putIfAbsent':
                    this.apiDbProxy?.putIfAbsent(data?.qco);
                    break;
                case 'confirm':
                    this.apiDbProxy?.confirm(data?.qco, data?.data);
                    break;
            }

            // Send reply so the proxy knows the message was handled. EAA-1644: reply to the
            // window that asked, pinned to its origin when it has one, instead of
            // broadcasting to whatever document the iframe happens to hold.
            try {
                const targetOrigin = e.origin && e.origin !== 'null' ? e.origin : '*';
                sourceWindow?.postMessage({ _scormReply: true, id, ok: true }, targetOrigin);
            } catch { /* cross-origin reply may fail, that's ok */ }
        };

        window.addEventListener('message', this.messageHandler);
    }

    /**
     * Get the full CMI data snapshot from scorm-again.
     */
    getCmiData(): Record<string, unknown> {
        if (!this.api) return {};
        try {
            return this.api.renderCMIToJSON() ?? {};
        } catch {
            return {};
        }
    }

    /**
     * Handle CMI commit — send data to backend.
     */
    private async handleCommit(): Promise<void> {
        if (this.destroyed || !this.api) return;

        const cmiData = this.getCmiData();
        try {
            const result: ScormCommitResult = await commitCmi(
                this.config.sessionId,
                this.config.sessionToken,
                'default',
                cmiData,
            );
            this.config.onCommit?.(result);
        } catch (error) {
            this.config.onError?.(error instanceof Error ? error : new Error(String(error)));
        }
    }

    /**
     * Handle completion event from SCORM content.
     */
    private handleCompletion(): void {
        if (this.destroyed || !this.api) return;

        // Commit first, then notify
        this.handleCommit().then(() => {
            const cmi = this.getCmiData() as any;
            const core = cmi?.cmi?.core ?? {};
            const score = core?.score ?? {};

            const event: ScormCompletionEvent = {
                completionStatus: core.lesson_status ?? 'unknown',
                scoreRaw: score.raw ? parseFloat(score.raw) : null,
                totalTime: core.total_time ?? '',
            };

            this.config.onComplete?.(event);
        });
    }

    /**
     * Clean up — stop auto-commit, remove API references, remove listeners.
     */
    destroy(): void {
        this.destroyed = true;

        if (this.commitTimer) {
            clearInterval(this.commitTimer);
            this.commitTimer = null;
        }

        // Remove postMessage listener
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
            this.messageHandler = null;
        }

        // Final commit before destroying
        if (this.api) {
            this.handleCommit();
        }

        // Clean up global API references
        if ((window as any).API === this.api) {
            delete (window as any).API;
        }
        if ((window as any).API_1484_11 === this.api) {
            delete (window as any).API_1484_11;
        }
        if ((window as any).API_DB === this.apiDbProxy) {
            delete (window as any).API_DB;
        }
        delete (window as any).getScormAPIForFrame;

        this.api = null;
        this.apiDbProxy = null;
    }
}
